import { Card, Flex, GlobalToken, Space } from 'antd';
import { memo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import {
  type ChatMessage,
  type MediaProjectInstanceResponse,
  type MessageOrigin,
} from '../../types';
import { StyledMarkdownWrapper } from './components/StyledMarkdownWrapper';
import { BotIcon } from './components/BotIcon';
import { Sources } from './Sources';
import { FollowUpQuestions } from './FollowUpQuestions';
import { ProcessingLabel } from './components/ProcessingLabel';

export const Message: React.FC<{
  message: ChatMessage;
  sendQuery: (text: string) => void;
  displaySource: (coordinates: MessageOrigin) => void;
  mainDocumentId?: string;
  publicationDocuments?: MediaProjectInstanceResponse['documents'];
  publication: MediaProjectInstanceResponse;
}> = memo(
  ({
    message,
    sendQuery,
    displaySource,
    mainDocumentId,
    publicationDocuments,
  }) => {
    const hasContent = (message?.messages?.[0]?.content?.length ?? 0) > 0;
    const hasMessage = message.message?.length > 0;

    // when undefined/null or true then acts like is finished otherwise it's false
    const isFinal = message.is_final !== false;

    // first message and sometimes message comes from messages content
    let messageContent: string;
    if (hasContent) messageContent = message?.messages?.[0]?.content ?? '';
    else if (hasMessage) messageContent = message.message;
    else messageContent = '';
    const isProcessing = !isFinal && !message.metadata;

    return (
      <Card
        title={message.query?.length > 0 ? message.query : null}
        style={{
          padding: 0,
          background: 'none',
          boxShadow: 'none',
          border: 'none',
          marginTop: (message?.query?.length ?? 0) > 0 ? 30 : 0,
        }}
        styles={{
          body: {
            padding: 0,
            border: 0,
            whiteSpace: 'pre-wrap',
            position: 'relative',
          },
          header: { border: 0 },
          title: {
            overflow: 'visible',
            whiteSpace: 'normal',
            marginLeft: -24,
            marginBottom: 30,
            fontSize: 16,
            fontWeight: 'normal',
          },
        }}
      >
        <StyledMessageContent align="start">
          <ProcessingLabel icon={<BotIcon />} loading={!messageContent} />
          {messageContent && (
            <StyledMarkdownWrapper>
              <Markdown remarkPlugins={[remarkGfm]}>
                {messageContent.replaceAll(/<br\/?>/gi, '\n')}
              </Markdown>
            </StyledMarkdownWrapper>
          )}
        </StyledMessageContent>
        <Flex vertical gap={30}>
          <Sources
            sources={message.metadata?.sources}
            isProcessing={isProcessing}
            displaySource={displaySource}
            mainDocumentId={mainDocumentId}
            publicationDocuments={publicationDocuments}
          />
          <FollowUpQuestions
            suggestedQuestions={message.metadata?.suggested_questions}
            isProcessing={isProcessing}
            sendQuery={sendQuery}
            isFinal={isFinal}
          />
        </Flex>
      </Card>
    );
  },
);

const StyledMessageContent = styled(Space)<{
  theme: GlobalToken;
}>`
  max-width: 100%;
  overflow: hidden;
  margin-bottom: 30px;

  p {
    margin: 0;
  }

  /* prevent line break at start of message */
  p:first-child {
    display: inline;
  }
`;

Message.displayName = 'Message';
